import { useTranslation } from 'react-i18next';
import { humanizeEnum } from '@/lib/utils';
import { Money, Row, SourceRow, Who, day, type RecordSource } from './bodyParts';

export interface SaleReturnDetail {
  id: string;
  returnNumber: string;
  returnType: string;
  status: string;
  totalRefund: number | string;
  notes?: string | null;
  rejectionReason?: string | null;
  createdAt: string;
  approvedAt?: string | null;
  createdByName?: string | null;
  approvedByName?: string | null;
  customerName?: string | null;
  sale?: RecordSource | null;
  items: Array<{ id: string; productName: string; quantity: number; unitPrice: number | string; totalAmount: number | string }>;
}

/** Read-only sale return for the record viewer (POS-I238). */
export function SaleReturnDetailBody({ data }: { data: SaleReturnDetail }) {
  const { t } = useTranslation('records');
  return (
    <div className="space-y-5 text-sm">
      <dl className="space-y-1.5">
        <Row label={t('rvDate')}><span dir="ltr">{day(data.createdAt)}</span></Row>
        <Row label={t('rvReturnType')}>{humanizeEnum(data.returnType)}</Row>
        {data.customerName && <Row label={t('rvCustomer')}>{data.customerName}</Row>}
      </dl>
      {data.items.length === 0 ? (
        <p className="text-muted-foreground">{t('rvNoItems')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="py-1 text-start font-medium">{t('rvProduct')}</th>
                <th className="py-1 text-end font-medium">{t('rvQty')}</th>
                <th className="py-1 text-end font-medium">{t('rvLineTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((i) => (
                <tr key={i.id} className="border-b align-top last:border-0">
                  <td className="py-1.5 break-words">{i.productName}</td>
                  <td className="py-1.5 text-end tabular-nums" dir="ltr">{i.quantity}</td>
                  <td className="py-1.5 text-end"><Money value={i.totalAmount} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t font-semibold">
                <td className="py-1.5" colSpan={2}>{t('rvRefund')}</td>
                <td className="py-1.5 text-end"><Money value={data.totalRefund} /></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <dl className="space-y-1.5">
        <Who label={t('rvCreatedBy')} name={data.createdByName} at={data.createdAt} />
        <Who label={t('rvApprovedBy')} name={data.approvedByName} at={data.approvedAt} />
        {data.rejectionReason && <Row label={t('rvRejectionReason')}>{data.rejectionReason}</Row>}
        {data.notes && <Row label={t('rvNotes')}>{data.notes}</Row>}
      </dl>
      <SourceRow items={[{ label: t('rvInvoice'), source: data.sale }]} />
    </div>
  );
}
